import { CaixaArredondada } from '../../componentes/CaixaArredondada'
import { Giroflex, RetrovisorViatura, RodaViatura, texturaDeChevron, texturaDeLetreiro } from './pecasViatura'

/**
 * Unidade de resgate: furgão de teto alto, ~6 m × 2 m, no mesmo acabamento do
 * caminhão (roda com porca, faixa refletiva, giroflex, letreiro).
 *
 * Montado com a frente em −X, como o caminhão, e girado para a frente ficar em
 * +Z — a orientação que a viatura de resgate sempre teve na mesa.
 */

const PRETO = '#15181c'
const CROMO = '#d9dee4'
const VIDRO = '#5a7890'
const BRANCO = '#f4f6f8'

const semRaycast = () => null

const X_FRENTE = -3.0
const X_TRASEIRA = 2.95
const MEIA_LARGURA = 1.0
const Y_PISO = 0.62
const Y_TETO = 2.75
/** Onde termina o capô e começa o para-brisa. */
const X_CAPO = -2.35

export function Resgate3D({ cor = '#dc2626' }: { cor?: string }) {
  const letreiro = texturaDeLetreiro('RESGATE', '193')
  const chevron = texturaDeChevron()
  const xCaixa = (X_CAPO + 0.55 + X_TRASEIRA) / 2
  const comprimentoCaixa = X_TRASEIRA - (X_CAPO + 0.55)
  const vidro = <meshStandardMaterial color={VIDRO} metalness={0.3} roughness={0.1} envMapIntensity={1.4} />
  return (
    <group name="resgate-ambulancia" rotation={[0, Math.PI / 2, 0]}>
      {/* chassi e para-choques */}
      <mesh raycast={semRaycast} castShadow position={[0, 0.5, 0]}>
        <boxGeometry args={[X_TRASEIRA - X_FRENTE - 0.3, 0.2, 1.3]} />
        <meshStandardMaterial color={PRETO} roughness={0.7} />
      </mesh>
      <mesh raycast={semRaycast} castShadow position={[X_FRENTE + 0.05, 0.62, 0]}>
        <CaixaArredondada args={[0.22, 0.3, MEIA_LARGURA * 2 + 0.04]} raio={0.06} />
        <meshStandardMaterial color="#2a2f35" roughness={0.6} />
      </mesh>
      <mesh raycast={semRaycast} castShadow position={[X_TRASEIRA - 0.02, 0.58, 0]}>
        <CaixaArredondada args={[0.2, 0.24, MEIA_LARGURA * 2 + 0.04]} raio={0.05} />
        <meshStandardMaterial color="#2a2f35" roughness={0.6} />
      </mesh>

      {/* capô curto */}
      <mesh raycast={semRaycast} castShadow position={[(X_FRENTE + 0.1 + X_CAPO + 0.6) / 2, 1.0, 0]}>
        <CaixaArredondada args={[X_CAPO + 0.6 - X_FRENTE - 0.1, 0.7, MEIA_LARGURA * 2 - 0.04]} raio={0.16} />
        <meshStandardMaterial color={cor} roughness={0.3} metalness={0.15} />
      </mesh>
      {/* grade e faróis */}
      <mesh raycast={semRaycast} position={[X_FRENTE + 0.1, 0.98, 0]}>
        <boxGeometry args={[0.02, 0.3, 1.0]} />
        <meshStandardMaterial color="#1b1f24" roughness={0.6} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={i} raycast={semRaycast} position={[X_FRENTE + 0.088, 0.88 + i * 0.1, 0]}>
          <boxGeometry args={[0.02, 0.025, 0.96]} />
          <meshStandardMaterial color={CROMO} metalness={0.85} roughness={0.2} />
        </mesh>
      ))}
      {[-1, 1].map((lado) => (
        <mesh key={lado} raycast={semRaycast} position={[X_FRENTE + 0.12, 1.1, lado * 0.72]} rotation={[0, 0, 0.25]}>
          <boxGeometry args={[0.04, 0.2, 0.36]} />
          <meshStandardMaterial color="#fffbe8" emissive="#fff6d6" emissiveIntensity={0.9} />
        </mesh>
      ))}

      {/* carroceria de teto alto */}
      <mesh raycast={semRaycast} castShadow receiveShadow position={[xCaixa, (Y_PISO + Y_TETO) / 2 + 0.15, 0]}>
        <CaixaArredondada args={[comprimentoCaixa, Y_TETO - Y_PISO - 0.3, MEIA_LARGURA * 2]} raio={0.14} />
        <meshStandardMaterial color={cor} roughness={0.3} metalness={0.15} />
      </mesh>
      {/* teto branco */}
      <mesh raycast={semRaycast} position={[xCaixa, Y_TETO + 0.01, 0]}>
        <CaixaArredondada args={[comprimentoCaixa - 0.1, 0.05, MEIA_LARGURA * 2 - 0.08]} raio={0.02} />
        <meshStandardMaterial color={BRANCO} roughness={0.45} />
      </mesh>
      {/* para-brisa inclinado */}
      <mesh raycast={semRaycast} position={[X_CAPO + 0.38, 1.82, 0]} rotation={[0, 0, -0.42]}>
        <boxGeometry args={[0.02, 0.98, MEIA_LARGURA * 2 - 0.14]} />
        {vidro}
      </mesh>

      {[-1, 1].map((lado) => {
        const zFace = lado * (MEIA_LARGURA + 0.004)
        return (
          <group key={lado}>
            {/* janela da porta da cabine */}
            <mesh raycast={semRaycast} position={[X_CAPO + 1.05, 1.9, zFace]}>
              <boxGeometry args={[0.72, 0.6, 0.012]} />
              {vidro}
            </mesh>
            {/* porta corrediça: friso e janela fosca */}
            {[X_CAPO + 1.5, X_CAPO + 2.75].map((x) => (
              <mesh key={x} raycast={semRaycast} position={[x, 1.65, zFace]}>
                <boxGeometry args={[0.015, 1.9, 0.012]} />
                <meshStandardMaterial color="#8f1a1a" roughness={0.5} />
              </mesh>
            ))}
            <mesh raycast={semRaycast} position={[X_CAPO + 2.12, 2.05, zFace]}>
              <boxGeometry args={[0.85, 0.5, 0.012]} />
              <meshStandardMaterial color="#c9d4dc" metalness={0.2} roughness={0.35} />
            </mesh>
            <mesh raycast={semRaycast} position={[X_CAPO + 2.6, 1.5, zFace + lado * 0.012]}>
              <boxGeometry args={[0.04, 0.16, 0.02]} />
              <meshStandardMaterial color={CROMO} metalness={0.85} roughness={0.2} />
            </mesh>
            {/* faixas refletivas */}
            <mesh raycast={semRaycast} position={[(X_FRENTE + X_TRASEIRA) / 2 + 0.2, 1.05, lado * (MEIA_LARGURA + 0.01)]}>
              <boxGeometry args={[X_TRASEIRA - X_FRENTE - 0.7, 0.13, 0.006]} />
              <meshStandardMaterial color="#fde047" emissive="#fde047" emissiveIntensity={0.25} roughness={0.35} />
            </mesh>
            <mesh raycast={semRaycast} position={[(X_FRENTE + X_TRASEIRA) / 2 + 0.2, 0.94, lado * (MEIA_LARGURA + 0.01)]}>
              <boxGeometry args={[X_TRASEIRA - X_FRENTE - 0.7, 0.05, 0.006]} />
              <meshStandardMaterial color={BRANCO} emissive="#ffffff" emissiveIntensity={0.15} roughness={0.35} />
            </mesh>
            {/* letreiro e cruz */}
            {letreiro && (
              <mesh raycast={semRaycast} position={[X_TRASEIRA - 1.1, 1.75, zFace + lado * 0.004]} rotation={[0, lado > 0 ? 0 : Math.PI, 0]}>
                <planeGeometry args={[1.3, 0.65]} />
                <meshStandardMaterial map={letreiro} transparent roughness={0.4} />
              </mesh>
            )}
            <group position={[X_TRASEIRA - 1.1, 2.35, zFace + lado * 0.005]}>
              <mesh raycast={semRaycast}>
                <boxGeometry args={[0.36, 0.1, 0.006]} />
                <meshStandardMaterial color={BRANCO} />
              </mesh>
              <mesh raycast={semRaycast}>
                <boxGeometry args={[0.1, 0.36, 0.006]} />
                <meshStandardMaterial color={BRANCO} />
              </mesh>
            </group>
            <RetrovisorViatura posicao={[X_CAPO + 0.6, 1.75, lado * MEIA_LARGURA]} lado={lado as 1 | -1} />
            {/* holofote lateral no alto da carroceria */}
            <mesh raycast={semRaycast} position={[X_TRASEIRA - 0.4, Y_TETO - 0.15, lado * (MEIA_LARGURA + 0.03)]}>
              <boxGeometry args={[0.24, 0.12, 0.05]} />
              <meshStandardMaterial color="#e5e7eb" emissive="#ffffff" emissiveIntensity={0.35} />
            </mesh>
          </group>
        )
      })}

      {/* traseira: duas portas com janela, lanternas verticais e chevron */}
      <group position={[X_TRASEIRA + 0.005, 0, 0]}>
        <mesh raycast={semRaycast} position={[0, 1.7, 0]}>
          <boxGeometry args={[0.012, 1.9, 0.015]} />
          <meshStandardMaterial color="#8f1a1a" />
        </mesh>
        {[-0.45, 0.45].map((z) => (
          <mesh key={z} raycast={semRaycast} position={[0, 2.15, z]}>
            <boxGeometry args={[0.012, 0.55, 0.6]} />
            {vidro}
          </mesh>
        ))}
        {[-1, 1].map((lado) => (
          <mesh key={lado} raycast={semRaycast} position={[0.006, 1.6, lado * 0.93]}>
            <boxGeometry args={[0.012, 0.9, 0.1]} />
            <meshStandardMaterial color="#b91c1c" emissive="#ef4444" emissiveIntensity={0.6} />
          </mesh>
        ))}
        <mesh raycast={semRaycast} position={[0.006, 1.0, 0]}>
          <boxGeometry args={[0.01, 0.32, MEIA_LARGURA * 2 - 0.3]} />
          <meshStandardMaterial color="#facc15" map={chevron ?? undefined} emissive="#facc15" emissiveIntensity={0.15} roughness={0.4} />
        </mesh>
      </group>

      <Giroflex posicao={[X_CAPO + 0.75, Y_TETO + 0.06, 0]} comprimento={1.6} />

      {[-1, 1].map((lado) => (
        <group key={lado}>
          <RodaViatura posicao={[X_FRENTE + 0.85, 0.38, lado * (MEIA_LARGURA - 0.13)]} raio={0.38} largura={0.24} lado={lado as 1 | -1} />
          <RodaViatura posicao={[X_TRASEIRA - 1.0, 0.38, lado * (MEIA_LARGURA - 0.13)]} raio={0.38} largura={0.24} lado={lado as 1 | -1} />
          {/* para-lamas */}
          {[X_FRENTE + 0.85, X_TRASEIRA - 1.0].map((x) => (
            <mesh key={x} raycast={semRaycast} position={[x, 0.82, lado * (MEIA_LARGURA - 0.01)]}>
              <CaixaArredondada args={[0.95, 0.08, 0.1]} raio={0.03} />
              <meshStandardMaterial color={PRETO} roughness={0.75} />
            </mesh>
          ))}
        </group>
      ))}
    </group>
  )
}
